import { type ReactNode } from 'react';
import * as React from 'react';
import { darken, rgba, saturate } from 'polished';
import styled from 'styled-components';

const FONT_SIZE = 18;
const LINE_HEIGHT = 32;
export const SPACING = 24;

const H2_FONT_SIZE = 28;
const H3_FONT_SIZE = 24;

const Wrapper = styled.div`
  font-family: 'IBM Plex Sans';

  h1:first-child,
  h2:first-child,
  h3:first-child {
    padding-top: 0;
  }

  *::selection {
    background: ${({ theme }) => rgba(theme.highlightColor, 0.3)};
  }

  p {
    font-size: ${FONT_SIZE}px;
    line-height: ${LINE_HEIGHT}px;
    margin-bottom: ${SPACING}px;
    font-weight: 400;
  }

  sup {
    font-size: 0.8em;
    line-height: 16px;
  }

  h1,
  h2,
  h3 {
    margin-bottom: ${SPACING}px;
    line-height: 1.2;
    padding-top: 15px;
    color: ${({ theme }) => theme.fontColor.LOUD};

    strong,
    b {
      font-weight: inherit;
    }

    a {
      color: ${({ theme }) => theme.highlightColor};
      border-color: ${({ theme }) => theme.highlightColor};
    }
  }

  h2 {
    font-size: ${H2_FONT_SIZE}px;
  }

  h3 {
    font-size: ${H3_FONT_SIZE}px;
  }

  ol,
  ul {
    font-size: ${FONT_SIZE}px;
    line-height: ${LINE_HEIGHT}px;
    margin-bottom: ${SPACING}px;
    padding-left: ${SPACING}px;
  }

  li {
    font-size: ${FONT_SIZE}px;
    line-height: ${LINE_HEIGHT}px;
    margin-bottom: 8px;

    > p {
      margin-bottom: 0;
    }
  }

  p,
  ul,
  ol {
    a {
      color: ${({ theme }) => theme.highlightColor};
      border-color: ${({ theme }) => theme.highlightColor};

      b {
        color: inherit;
      }

      u {
        text-decoration: none;
      }

      &:hover {
        text-decoration: none;
        background-color: ${({ theme }) => rgba(theme.highlightColor, 0.3)};
      }
    }
  }

  strong,
  b {
    font-weight: 600;
    color: ${({ theme }) => theme.fontColor.LOUD};
  }

  .blockquote,
  blockquote {
    margin-top: 10px;
    margin-bottom: 10px;
    margin-left: 30px;
    padding-left: 15px;
    border-left: 2px solid ${({ theme }) => theme.highlightColor};
  }

  pre {
    background-color: ${({ theme }) =>
      theme.appUI.backgroundColor.MUTED} !important;
    border-radius: 0;
    margin: 0 0 ${SPACING}px;
    padding: 10px 15px;

    > code {
      background-color: ${({ theme }) => theme.appUI.backgroundColor.MUTED};
      color: ${({ theme }) => theme.fontColor.LOUD};
      border: none;
      padding: 0;
    }
  }

  p > code {
    white-space: nowrap;
  }

  code {
    background-color: ${({ theme }) => theme.colors.GRAY};
    border: 1px solid ${({ theme }) => darken(0.15, theme.colors.GRAY)};
    padding: 2px 5px;
    color: ${({ theme }) => theme.colors.BLACK};
    border-radius: 0;
    font-size: ${({ theme }) => theme.fontSize.MEDIUM};
    white-space: pre-wrap;
    line-break: anywhere;

    .token.keyword {
      color: ${({ theme }) => theme.colors.LIGHT_PINK};
    }

    .token.string,
    .token.builtin,
    .token.class-name {
      color: ${({ theme }) => theme.colors.LIGHT_PURPLE};
    }
  }

  pre code {
    border: none;
    padding: 0;
  }

  pre i code {
    color: ${({ theme }) => saturate(0.1, darken(0.1, theme.palette.jade))};
    font-style: normal;
  }

  img {
    display: flex;
    max-width: 100%;
  }

  figcaption {
    text-align: center;
    margin-top: ${SPACING}px;
    margin-bottom: ${SPACING}px;
    opacity: 0.6;
    padding: 0 10px;
  }

  .gatsby-resp-image-image {
    height: auto !important;
  }

  .gatsby-resp-image-link {
    border-bottom: none !important;
  }

  .footnotes {
    ol,
    ul {
      padding: 0;
    }
  }
`;

interface RunningTextProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const RunningText: React.ComponentType<RunningTextProps> = React.memo(
  ({ children, className, style }) => {
    return (
      <Wrapper className={className} style={style}>
        {children}
      </Wrapper>
    );
  }
);
